/**
 * PublicLayout - 门户布局容器
 * 
 * 与登录页统一的 Apple 风格设计：
 * - 渐变背景 + 动画光球效果
 * - 毛玻璃头部和侧边栏
 * - 响应式布局
 * - 明暗模式
 */

import React, { useEffect } from 'react';
import PublicSidebar from './PublicSidebar';
import PublicHeader from './PublicHeader';
import PublicMobileTabBar from './PublicMobileTabBar';
import Footer from './Footer';
import CookieBanner from './CookieBanner';
import { Outlet } from 'react-router-dom';
import { useThemeStore } from '../../stores/theme-store';
import { cn } from '../../utils/cn';
import { Tag } from '../../types';

interface PublicLayoutProps {
  children?: React.ReactNode;
  // 筛选状态管理
  selectedTags?: string[];
  selectedCategory?: string | null;
  onTagToggle?: (tagId: string) => void;
  onCategorySelect?: (categoryId: string | null) => void;
  onResetFilters?: () => void;
  // 订单查询
  onOrderQuery?: (orderNumber: string) => void;
  // 侧边栏刷新触发器
  sidebarRefreshTrigger?: number;
  // 用户信息
  user?: any;
  isAuthenticated?: boolean;
  // 视图切换
  currentView?: 'files' | 'profile' | 'order';
  onViewChange?: (view: 'files' | 'profile' | 'order') => void;
  availableTags?: Tag[];
}

const PublicLayout: React.FC<PublicLayoutProps> = ({ 
  children, 
  selectedTags,
  selectedCategory,
  onTagToggle,
  onCategorySelect,
  onResetFilters,
  onOrderQuery,
  sidebarRefreshTrigger,
  user,
  isAuthenticated,
  currentView,
  onViewChange,
  availableTags
}) => {
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = React.useState(false);
  
  // 初始化主题
  const { initTheme, resolvedTheme } = useThemeStore();
  
  useEffect(() => {
    initTheme();
  }, [initTheme]);

  const toggleSidebar = () => {
    setSidebarCollapsed((v) => !v);
  };

  const toggleMobileSidebar = () => {
    setIsMobileSidebarOpen((v) => !v);
  };

  React.useEffect(() => {
    // Disable background scroll when mobile drawer is open
    if (isMobileSidebarOpen) {
      const { overflow } = document.body.style as { overflow: string };
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = overflow || '';
      };
    }
  }, [isMobileSidebarOpen]);

  // 根据主题决定是否添加 light-mode 类
  const isLightMode = resolvedTheme === 'light';

  return (
    <div className={cn(
      'portal-background flex h-screen overflow-hidden',
      isLightMode && 'light-mode'
    )}>
      {/* 动画渐变光球 - 与登录页统一 */}
      <div className="portal-orb portal-orb-1" />
      <div className="portal-orb portal-orb-2" />
      <div className="portal-orb portal-orb-3" />
      
      {/* 桌面端侧边栏 */}
      <PublicSidebar 
        collapsed={sidebarCollapsed} 
        mobileOpen={isMobileSidebarOpen} 
        onToggle={toggleSidebar}
        onMobileToggle={toggleMobileSidebar}
        selectedTags={selectedTags}
        selectedCategory={selectedCategory}
        onTagToggle={onTagToggle}
        onCategorySelect={onCategorySelect}
        onResetFilters={onResetFilters}
        onOrderQuery={onOrderQuery}
        refreshTrigger={sidebarRefreshTrigger}
        user={user}
        isAuthenticated={isAuthenticated}
        availableTags={availableTags}
      />
      
      <div className="flex flex-col flex-1 overflow-hidden h-full relative z-10">
        <PublicHeader 
          toggleSidebar={toggleSidebar} 
          sidebarCollapsed={sidebarCollapsed}
          toggleMobileSidebar={toggleMobileSidebar}
          mobileSidebarOpen={isMobileSidebarOpen}
          onViewChange={onViewChange}
        />
        <div className="portal-main flex-1 overflow-y-auto flex flex-col custom-scrollbar">
          <main className="flex-1 p-4 md:p-6 pb-20 md:pb-6">
            {children || <Outlet />}
          </main>
          <Footer />
        </div>
      </div>
      
      {/* 移动端底部导航栏 */}
      <PublicMobileTabBar 
        user={user}
        isAuthenticated={isAuthenticated}
        currentView={currentView}
        onViewChange={onViewChange}
        onOrderQuery={onOrderQuery}
      />
      
      {/* Cookie横幅 */}
      <CookieBanner />
    </div>
  );
};

export default PublicLayout;
